/**
 * ImportMeetingContentModal — 逐场导入会议内容。
 *
 * 会议平台同步回来的只是清单信息（标题、时间、组织者），纪要要在这里逐场导入。
 * 两条通路：向会议平台拉取纪要，或直接粘贴纪要正文。录音转写不在这里，入口是
 * 会议详情里的录音上传。
 */

import { useState } from "react";
import { Alert, Input, Modal, Radio, Space, Typography } from "antd";
import type { Meeting, MeetingContentImportRequest } from "../../types/meeting";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

type ImportChoice = "platform_minutes" | "manual_paste";

interface ImportMeetingContentModalProps {
  meeting: Meeting | null | undefined;
  open: boolean;
  submitting: boolean;
  /** 上一次导入失败的原因；为 null 时不显示。 */
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (data: MeetingContentImportRequest) => void;
}

export default function ImportMeetingContentModal({
  meeting,
  open,
  submitting,
  errorMessage,
  onClose,
  onSubmit,
}: ImportMeetingContentModalProps) {
  // 手动创建的会议没有平台纪要可拉，只能粘贴正文。
  const platformAvailable =
    !!meeting && meeting.source !== "manual" && !!meeting.externalMeetingId;
  const [choice, setChoice] = useState<ImportChoice>(
    platformAvailable ? "platform_minutes" : "manual_paste",
  );
  const [text, setText] = useState("");

  // 每次打开都回到初始选择、清空正文。用渲染期比对上一次的开合状态而不是副作用：
  // 副作用里改 state 会多渲染一轮，用户会看见上一次残留的正文一闪而过。
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setChoice(platformAvailable ? "platform_minutes" : "manual_paste");
      setText("");
    }
  }

  const canSubmit =
    choice === "platform_minutes" ? platformAvailable : text.trim().length > 0;

  const handleOk = () => {
    if (choice === "platform_minutes") {
      onSubmit({ transcriptSource: "platform_minutes" });
      return;
    }
    onSubmit({ transcriptSource: "manual_paste", text, lang: "zh" });
  };

  return (
    <Modal
      title="导入会议内容"
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText="导入"
      okButtonProps={{
        disabled: !canSubmit,
        "data-testid": "meeting-import-submit",
      }}
      confirmLoading={submitting}
      destroyOnHidden
      width={640}
    >
      <Paragraph type="secondary">
        这场会目前只有清单信息。导入内容之后才会有纪要、摘要与待办。
      </Paragraph>
      {errorMessage && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: "var(--jerry-sp-3)" }}
          data-testid="meeting-import-error"
          message="导入未完成"
          description={errorMessage}
        />
      )}
      <Radio.Group
        value={choice}
        onChange={(e) => setChoice(e.target.value as ImportChoice)}
        style={{ width: "100%" }}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Radio
            value="platform_minutes"
            disabled={!platformAvailable}
            data-testid="meeting-import-choice-platform"
          >
            从会议平台拉取纪要
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {platformAvailable
                ? "平台纪要还在生成时这次会拉不到内容，稍后重试即可。"
                : "这场会不是从会议平台同步来的，没有平台纪要可拉取。"}
            </Text>
          </Radio>
          <Radio value="manual_paste" data-testid="meeting-import-choice-manual">
            粘贴纪要正文
          </Radio>
        </Space>
      </Radio.Group>
      {choice === "manual_paste" && (
        <TextArea
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoSize={{ minRows: 8, maxRows: 18 }}
          placeholder="粘贴会议纪要或转录正文…"
          style={{ marginTop: "var(--jerry-sp-3)" }}
          aria-label="纪要正文"
          data-testid="meeting-import-text"
        />
      )}
    </Modal>
  );
}
